import { useNavigate, useParams } from 'react-router-dom';

import { useOperatorsState } from '../../../services/OperatorsList';
import './DetailsCpe.scss';
import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { format, formatDistanceToNowStrict } from 'date-fns';
import { enGB, es, srLatn, sv } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import {
  ArrowUpRight,
  Ban,
  Check,
  CircleCheck,
  Clock,
  Copy,
  Cpu,
  LucideIcon,
  MonitorSmartphone,
  Play,
  Plus,
  ShieldAlert,
  ShieldCheck,
  Smartphone,
  Square,
  UserRound,
  X,
} from 'lucide-react';

import { useToast } from '../../../services/ToastContext';

import ConfirmationModal from '../../../components/pop-up/PopUpAlert';

// date-fns locale per UI language (display-only relative times)
const DATE_FNS_LOCALES: Record<string, Locale> = { en: enGB, sr: srLatn, es, sv };

type Props = {
  summaryData: any;
  emailSuccess: any;
  onTabSelect: (tabName: string) => void;
};

export const DetailsCpe = (props: Props) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { operatorList, loggedUserData } = useOperatorsState();
  const { operatorId, cpeId } = useParams();
  const [broadbandSecurity, setBroadbandSecurity] = useState(true);
  const [mobileSecurity, setMobileSecurity] = useState(true);
  const [mdm, setMdm] = useState(false);

  const [threatsCount, setThreatsCount] = useState(0);
  const [routerCount, setRouterCount] = useState(0);
  const [devicesCount, setDevicesCount] = useState(0);
  const [devicesWithApp, setDevicesWithApp] = useState(0);
  let [gracePeriod, setGracePeriod] = useState<boolean>(false);
  let [cyberShield, setCyberShield] = useState<boolean>(false);
  let [adBlock, setAdBlock] = useState<boolean>(false);
  const [globalAllow, setGlobalAllow] = useState<any[]>([]);
  const [globalBlocked, setGlobalBlocked] = useState<any[]>([]);
  const [currentTypedWhitelist, setCurrentTypedWhitelist] = useState<string>('');
  const [currentTypedBlocklist, setCurrentTypedBlocklist] = useState<string>('');
  const { showToastMessage } = useToast();

  const [error, setError] = useState('');
  const [errorW, setWError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const fetchSettings = async () => {
    try {
      const data = await axios.get(`v2/op/master-operator/${cpeId}/settings`);
      setGracePeriod(data.data?.gracePeriod);
      setCyberShield(data.data?.cyberShield);
      setAdBlock(data.data?.adBlock);
    } catch (error) {
      console.error('Error fetching router settings: ', error);
    }
  };

  async function fetchWhitelistFromApi() {
    const fetchGlobalAllow = await axios.get(`v2/op/master-operator/${cpeId}/global/allowed`);
    setGlobalAllow(fetchGlobalAllow.data?.domains);
  }

  async function fetchBlacklistFromApi() {
    const fetchGlobalBlocked = await axios.get(`v2/op/master-operator/${cpeId}/global`);
    setGlobalBlocked(fetchGlobalBlocked.data?.domains);
  }

  useEffect(() => {
    fetchSettings();
    const intervalId = setInterval(fetchSettings, 900000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    fetchWhitelistFromApi();
    fetchBlacklistFromApi();
  }, [cpeId]);

  async function updateAllowDevices(allowAllDevices: boolean) {
    gracePeriod = allowAllDevices;
    let period = !gracePeriod ? 0 : 10000;
    console.log(period);
    await axios.patch(`v2/op/master-operator/2/grace-period/`, { gracePeriod: period }).then(() => {
      showToastMessage(t('cpeDetails.details.toasts.gracePeriodUpdated'));
    });
    setGracePeriod(allowAllDevices);
  }

  async function updateShieldSettings(blackdiceShieldValue: boolean) {
    await axios.patch(`v2/op/master-operator/${cpeId}/feat?adBlock=` + adBlock + '&cyberShield=' + blackdiceShieldValue);
    setCyberShield(blackdiceShieldValue);
    // ToastMessage('success', t('menu.success'));
    showToastMessage(t('cpeDetails.details.toasts.cyberShieldUpdated'));
  }

  async function updateAdBlockerSettings(adBlockerValue: boolean) {
    await axios.patch(`v2/op/master-operator/${cpeId}/feat?adBlock=` + adBlockerValue + '&cyberShield=' + cyberShield);
    showToastMessage(t('cpeDetails.details.toasts.adBlockUpdated'));
    setAdBlock(adBlockerValue);
  }

  const filterValidation = (website: string) => {
    if (website.includes('.')) {
      const index0 = website.replace(/\s+/g, '').split('.').slice(-2)[0];
      const index1 = website.split('.').slice(-2)[1];
      const index0Length = index0.length;
      const index1Length = index1.length;
      if (index0Length === 0) {
        return 'cpeDetails.details.validation.firstPartEmpty';
      }
      if (index1Length >= 2) {
        return true;
      } else {
        return 'cpeDetails.details.validation.invalid';
      }
    } else {
      return 'cpeDetails.details.validation.enterDomain';
    }
  };

  const addToWhitelistFilters = async () => {
    if (currentTypedWhitelist === '') {
      showToastMessage(t('cpeDetails.details.toasts.allowedEmpty'));
      return;
    }
    const lowercaseBlocklistFilters = globalBlocked.map((item) => item.toLowerCase());
    const lowercaseWhitelistFilters = globalAllow.map((item) => item.toLowerCase());
    const blockedDomain = lowercaseBlocklistFilters.includes(currentTypedWhitelist.toLowerCase());
    const allowedDomain = lowercaseWhitelistFilters.includes(currentTypedWhitelist.toLowerCase());

    const blockedWhiteList = lowercaseBlocklistFilters.includes(currentTypedWhitelist.toLowerCase());

    if (blockedWhiteList) {
      showToastMessage(t('cpeDetails.details.toasts.alreadyBlocked', { domain: currentTypedWhitelist }));
      return;
    }

    if (!blockedDomain && !allowedDomain) {
      const validation = filterValidation(currentTypedWhitelist);
      if (validation == true) {
        const newWhitelist = [...globalAllow, currentTypedWhitelist];
        setGlobalAllow(newWhitelist);
        if (inputRef.current) {
          inputRef.current.value = '';
          setCurrentTypedWhitelist('');
          updateWhiteListFilters(newWhitelist);
        }
      } else {
        setWError(validation);
      }
    } else {
      showToastMessage(t('cpeDetails.details.toasts.domainExists'));
    }
  };

  const updateWhiteListFilters = async (newWhitelist: any) => {
    const filterPayload = {
      isActive: true,
      domains: newWhitelist,
    };
    try {
      await axios.patch(`v2/op/master-operator/${cpeId}/global/allowed`, filterPayload);
      showToastMessage(t('cpeDetails.details.toasts.updated'));
    } catch (error) {
      console.error('Error updating Whitelist filters:', error);
    }
  };

  const updateBlockListFilters = async (newBlocklist: any) => {
    const filterPayload = {
      isActive: true,
      domains: newBlocklist,
    };
    try {
      await axios.patch(`v2/op/master-operator/${cpeId}/global`, filterPayload);
      showToastMessage(t('cpeDetails.details.toasts.updated'));
    } catch (error) {
      console.error('Error updating Blocklist filters:', error);
    }
  };

  const removeWhitelist = (filter: string) => {
    setGlobalAllow((prevWhitelist) => {
      const updatedWhitelist = prevWhitelist.filter((item) => item !== filter);
      updateWhiteListFilters(updatedWhitelist);
      setGlobalAllow((prev) => prev.filter((item) => item !== filter));
      return updatedWhitelist;
    });
  };

  const removeBlocklist = (filter: string) => {
    setGlobalBlocked((prevBlocklist) => {
      const updatedBlocklist = prevBlocklist.filter((item) => item !== filter);
      updateBlockListFilters(updatedBlocklist);
      setGlobalBlocked((prev) => prev.filter((item) => item !== filter));
      return updatedBlocklist;
    });
  };

  const addBlockedDomain = (e: string) => {
    setError('');
    setCurrentTypedBlocklist(e);
  };
  const addAllowedDomain = (e: string) => {
    setWError('');
    setCurrentTypedWhitelist(e);
  };

  const addToBlocklistFilters = () => {
    if (currentTypedBlocklist === '') {
      showToastMessage(t('cpeDetails.details.toasts.blockedEmpty'));
      return;
    }

    const lowercaseBlocklistFilters = globalBlocked.map((item) => item.toLowerCase());
    const lowercaseWhitelistFilters = globalAllow.map((item) => item.toLowerCase());
    const blockedDomainList = lowercaseWhitelistFilters.includes(currentTypedBlocklist.toLowerCase());

    if (blockedDomainList) {
      showToastMessage(t('cpeDetails.details.toasts.alreadyAllowed', { domain: currentTypedBlocklist }));
      return;
    }

    if (!lowercaseBlocklistFilters.includes(currentTypedBlocklist.toLowerCase())) {
      const validation = filterValidation(currentTypedBlocklist);
      if (validation == true) {
        const newBlocklist = [...globalBlocked, currentTypedBlocklist];
        setGlobalBlocked(newBlocklist);
        updateBlockListFilters(newBlocklist);
        if (inputRef.current) {
          inputRef.current.value = '';
          setCurrentTypedBlocklist('');
        }
      } else {
        setError(validation);
      }
    } else {
      showToastMessage(t('cpeDetails.details.toasts.domainExists'));
    }
  };

  const [isAgentRunning, setIsAgentRunning] = useState<boolean>(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
  const [confirmationMessage, setConfirmationMessage] = useState<string>('');
  const [confirmationTitle, setConfirmationTitle] = useState<string>('');
  const [confirmationAction, setConfirmationAction] = useState<'start' | 'stop' | ''>('');

  const handleStartBootloader = () => {
    setConfirmationAction('start');
    setConfirmationTitle(t('cpeDetails.details.agent.confirmStartTitle'));
    setConfirmationMessage(t('cpeDetails.details.agent.confirmStartMessage'));
    setShowConfirmationModal(true);
  };

  const handleStopBootloader = () => {
    setConfirmationAction('stop');
    setConfirmationTitle(t('cpeDetails.details.agent.confirmStopTitle'));
    setConfirmationMessage(t('cpeDetails.details.agent.confirmStopMessage'));
    setShowConfirmationModal(true);
  };

  const handleBootloaderSubmit = async () => {
    try {
      if (isAgentRunning) {
        await axios.patch(`/v2/op/master-operator/start-stop/agent?cpeId=${cpeId}&isActive=false&type=cpe`);
      } else {
        await axios.patch(`/v2/op/master-operator/start-stop/agent?cpeId=${cpeId}&isActive=true&type=cpe`);
      }
    } catch (error) {
      console.error('Error stopping bootloader agent: ', error);
    }
  };
  useEffect(() => {
    getThreats();
    getRouters();
    getDevices();
  }, []);

  async function getThreats() {
    // const resp = await axios.get('/op/dashboard/threats-mitigated?days=1');
    // let totalCount = resp.data.amount;
    // setThreatsCount(totalCount);
    const threatsMitigated = await axios.get(`/v2/op/router/${cpeId}/device-threats?days=1`);
    setThreatsCount(threatsMitigated.data.amount);

    console.log(props.summaryData, 'props.summaryData');
    setSummaryData(props.summaryData);
  }
  async function getRouters() {
    setRouterCount(1);
  }
  async function getDevices() {
    const resp = await axios.get(`/op/17/cpe/${cpeId}/device_table`);
    let totalCount = resp.data.records;
    setDevicesCount(totalCount);
    let devicesWithApp = 0;
    for (let i = 0; i < resp.data.data.length; i++) {
      if (resp.data.data[i].deviceType === 'Smartphone') {
        devicesWithApp = devicesWithApp + 1;
      }
    }
    setDevicesWithApp(devicesWithApp);
  }

  function getAccountInformation() {
    const opId = loggedUserData?.operatorId;
    const last_seen = format(new Date(props.summaryData?.last_seen), 'dd-MMM-yyyy HH:mm:ss').toUpperCase();
    return [
      { key: 'Account Owner', value: props.summaryData?.registrant, showUpdate: true },
      { key: 'Alternate Email', value: props.summaryData?.alternativeRegistrant, showUpdate: true },
      { key: 'Last Activity', value: last_seen || '-', showUpdate: false },

      //  { key: 'Registered to', value: summaryData?.registrant, showUpdate: true },
      //{ key: 'Verified', value: props.summaryData?.verified },
    ];
  }

  function getDateInformation() {
    const opId = loggedUserData?.operatorId;
    const registered = format(new Date(props.summaryData?.registered), 'dd-MMM-yyyy HH:mm:ss').toUpperCase();
    const verified = format(new Date(props.summaryData?.dateVerified), 'dd-MMM-yyyy HH:mm:ss').toUpperCase();
    return [
      { key: 'Date Registered', value: registered },
      { key: 'Date Verified', value: verified || '-' },
      //  { key: 'Registered to', value: summaryData?.registrant, showUpdate: true },
      //{ key: 'Verified', value: props.summaryData?.verified },
    ];
  }

  /*   function getActivityInformation() {
    console.log('here');
    return [
      { key: 'Firmware version', value: props.summaryData?.firmwareVersion },
      { key: 'Last restart', value: props.summaryData?.last_boot_local },
      { key: 'Timezone offset (from UTC)', value: props.summaryData?.time_zone_offset },
    ];
  }

  function getGlobalProtections() {
    return [
      { key: 'BlackDice Shield', value: props.summaryData?.shield_active ? 'Active' : 'Inactive' },
      { key: 'BlackDice ABC', value: props.summaryData?.abc_active ? 'Active' : 'Inactive' },
    ];
  }

  function getDevicesByType() {
    return props.summaryData?.device_types.map((item: any) => {
      return { type: item[0], count: item[1] };
    });
  }

  function goToDevicePage(rowData: any, highlightCve?: boolean) {
    navigate(
      `/operators/${operatorId}/cpe/${cpeId}/d/${rowData.key.ID || rowData.key.deviceId}`,
      highlightCve ? { state: { id: 'high-cve' } } : undefined
    );
  } */

  function handleChangeBroadbandSecurity(checked: any) {
    setBroadbandSecurity(checked);
  }

  function handleChangeMobileSecurity(checked: any) {
    setMobileSecurity(checked);
  }

  function handleChangeMdm(checked: any) {
    console.log(checked);
    setMdm(checked);
  }

  function getAccountRoutersInfo() {
    return [
      { key: 'Routers With Significant Issues', value: 0 },
      { key: 'Routers WIth Minor Issues', value: 0 },
      { key: 'Routers With No Issues', value: 1 },
    ];
  }

  function getAccountDevicesInfo() {
    return [
      { key: 'Devices With Significant Issues', value: 0 },
      { key: 'Devices With Minor Issues', value: 0 },
      { key: 'Devices With No Issues', value: devicesCount },
    ];
  }

  function getMobilesWithApplicationInfo() {
    return [
      { key: 'Applications That Require Upgrade', value: 0 },
      { key: 'Applications That Should Be Upgraded', value: 0 },
      { key: 'Applications That Are Up To Date', value: devicesWithApp },
    ];
  }

  const [threatPage, setThreatPage] = useState<boolean>(false);
  const [routerPage, setRouterPage] = useState<boolean>(false);
  const [devicePage, setDevicePage] = useState<boolean>(false);

  const handleThreatPageSelect = () => {
    setThreatPage(true);
    props.onTabSelect('Threats');
  };

  const handleRouterPageSelect = () => {
    setRouterPage(true);
    props.onTabSelect('Routers');
  };

  const handleDevicePageSelect = () => {
    setDevicePage(true);
    props.onTabSelect('Devices');
  };

  const [summaryData, setSummaryData] = useState<any>();
  const [selectedMenu, setSelectedMenu] = useState('Details');
  function handleMenuClick(menuName: string) {
    setSelectedMenu(menuName);
  }

  function convertToUtc(dateString?: string) {
    if (!dateString || typeof dateString !== 'string') {
      return 'na';
    }

    try {
      let date = new Date(dateString);

      return format(date, 'dd-MM-yyyy HH:mm:ss');
    } catch (error) {
      console.error('Error converting date:', error);
      return 'na';
    }
  }

  // Local copy is only set after the threats request resolves; fall back to the page's data meanwhile
  const account = summaryData ?? props.summaryData;

  const isRouterActive =
    !!account?.last_seen &&
    (new Date().getTime() - new Date(account.last_seen).getTime()) / (1000 * 60 * 60) < 1;

  // "3h ago" helper for timeline rows
  const relative = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return formatDistanceToNowStrict(date, { addSuffix: true, locale: DATE_FNS_LOCALES[i18n.language] || enGB });
  };

  const [copiedKey, setCopiedKey] = useState<string>('');
  const copyValue = async (key: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      showToastMessage(t('cpeDetails.details.copied', { label: key }));
      window.setTimeout(() => setCopiedKey((current) => (current === key ? '' : current)), 1500);
    } catch (error) {
      console.error('Clipboard write failed:', error);
    }
  };

  const renderRow = (label: string, value: any, mono = true, opts: { copy?: boolean; sub?: string } = {}) => {
    const empty = value === undefined || value === null || value === '' || value === 'na';
    const display = empty ? t('cpeDetails.details.notAvailable') : value;
    return (
      <div className="dc-row">
        <dt className="dc-row-label">{label}</dt>
        <dd className="dc-row-value-wrap">
          <span className={`dc-row-value bd-truncate${mono ? ' bd-mono' : ''}`} title={String(display)}>
            {display}
          </span>
          {opts.sub && !empty && <span className="dc-row-sub">{opts.sub}</span>}
          {opts.copy && !empty && (
            <button
              type="button"
              className={`dc-copy${copiedKey === label ? ' done' : ''}`}
              onClick={() => copyValue(label, String(value))}
              aria-label={t('cpeDetails.details.copy', { label })}
              title={t('cpeDetails.details.copy', { label })}
            >
              {copiedKey === label ? <Check size={13} strokeWidth={2.4} /> : <Copy size={13} />}
            </button>
          )}
        </dd>
      </div>
    );
  };

  // Overview tiles — data already fetched by getThreats / getDevices; tiles jump to their tab
  const kpis: { label: string; value: number | string; icon: LucideIcon; tone?: string; onClick?: () => void }[] = [
    { label: t('cpeDetails.details.kpis.threatsBlockedToday'), value: threatsCount ?? 0, icon: ShieldAlert, tone: 'danger', onClick: handleThreatPageSelect },
    { label: t('cpeDetails.details.kpis.devicesOnRouter'), value: devicesCount ?? 0, icon: MonitorSmartphone, onClick: handleDevicePageSelect },
    { label: t('cpeDetails.details.kpis.smartphones'), value: devicesWithApp ?? 0, icon: Smartphone },
    {
      label: t('cpeDetails.details.kpis.protection'),
      value: cyberShield ? (adBlock ? t('cpeDetails.details.kpis.shieldAdBlock') : t('cpeDetails.details.kpis.shieldOn')) : t('cpeDetails.details.kpis.shieldOff'),
      icon: cyberShield ? ShieldCheck : ShieldAlert,
      tone: cyberShield ? '' : 'warn',
    },
  ];

  const serviceSettings = [
    {
      id: 'cyber-shield-switch',
      label: t('cpeDetails.details.services.threatProtection'),
      desc: t('cpeDetails.details.services.threatProtectionDesc'),
      checked: cyberShield,
      onChange: (e: any) => updateShieldSettings(e),
    },
    {
      id: 'ad-block-switch',
      label: t('cpeDetails.details.services.adBlocking'),
      desc: t('cpeDetails.details.services.adBlockingDesc'),
      checked: adBlock,
      onChange: (e: any) => updateAdBlockerSettings(e),
    },
    {
      id: 'grace-period-switch',
      label: t('cpeDetails.details.services.autoApprove'),
      desc: t('cpeDetails.details.services.autoApproveDesc'),
      checked: gracePeriod,
      onChange: (e: any) => updateAllowDevices(e),
    },
  ];

  return (
    <div className="details-cpe dc-details">
      {/* Section header */}
      <header className="dc-hd">
        <div className="dc-hd-text">
          <h2 className="bd-h2">{t('cpeDetails.details.title')}</h2>
          <p className="bd-body">{t('cpeDetails.details.subtitle')}</p>
        </div>
        <div className="dc-hd-status">
          <span className={`bd-chip ${isRouterActive ? 'mint' : 'warn'}`}>
            <span className="dot" />
            {isRouterActive ? t('cpeDetails.details.active') : t('cpeDetails.details.inactive')}
          </span>
          {account?.last_seen && <span className="bd-meta">{t('cpeDetails.details.lastSeenRelative', { time: relative(account.last_seen) })}</span>}
        </div>
      </header>

      {/* Overview tiles */}
      <div className="dc-kpis">
        {kpis.map(({ label, value, icon: Icon, tone, onClick }) => {
          const content = (
            <>
              <span className="bd-kpi-icon">
                <Icon size={18} strokeWidth={1.75} />
              </span>
              <span className="bd-kpi-text">
                <span className="bd-kpi-label" title={label}>
                  {label}
                </span>
                <span className={`bd-kpi-num ${typeof value === 'number' ? 'bd-stat-num' : 'dc-kpi-text-value'}`}>
                  {typeof value === 'number' ? value.toLocaleString() : value}
                </span>
              </span>
              {onClick && <ArrowUpRight size={16} className="bd-kpi-arrow" />}
            </>
          );
          return onClick ? (
            <button key={label} type="button" className={`bd-card bd-kpi interactive dc-kpi ${tone || ''}`} onClick={onClick}>
              {content}
            </button>
          ) : (
            <div key={label} className={`bd-card bd-kpi dc-kpi ${tone || ''}`}>
              {content}
            </div>
          );
        })}
      </div>

      {/* Key / value groups */}
      <div className="dc-groups">
        <section className="dc-card">
          <div className="dc-card-hd">
            <span className="dc-card-icon">
              <UserRound size={16} strokeWidth={1.75} />
            </span>
            <h3 className="bd-h3">{t('cpeDetails.details.account.title')}</h3>
          </div>
          <dl className="dc-rows">
            {renderRow(t('cpeDetails.details.account.registrant'), account?.registrant, false, { copy: true })}
            {renderRow(t('cpeDetails.details.account.accountIdentifier'), account?.accountId, true, { copy: true })}
            {renderRow(t('cpeDetails.details.account.accountId'), account?.id, true, { copy: true })}
            {renderRow(t('cpeDetails.details.account.operator'), account?.operator, false)}
          </dl>
        </section>

        <section className="dc-card">
          <div className="dc-card-hd">
            <span className="dc-card-icon">
              <Cpu size={16} strokeWidth={1.75} />
            </span>
            <h3 className="bd-h3">{t('cpeDetails.details.router.title')}</h3>
          </div>
          <dl className="dc-rows">
            {renderRow(t('cpeDetails.details.router.serialNumber'), account?.serial, true, { copy: true })}
            {renderRow(t('cpeDetails.details.router.macAddress'), account?.mac, true, { copy: true })}
            {renderRow(t('cpeDetails.details.router.firmwareVersion'), account?.firmwareVersion)}
          </dl>
        </section>

        <section className="dc-card">
          <div className="dc-card-hd">
            <span className="dc-card-icon">
              <Clock size={16} strokeWidth={1.75} />
            </span>
            <h3 className="bd-h3">{t('cpeDetails.details.timeline.title')}</h3>
          </div>
          <dl className="dc-rows">
            {renderRow(t('cpeDetails.details.timeline.lastSeen'), convertToUtc(account?.last_seen_utc), true, { sub: relative(account?.last_seen_utc) })}
            {renderRow(t('cpeDetails.details.timeline.lastReboot'), convertToUtc(account?.last_boot_utc), true, { sub: relative(account?.last_boot_utc) })}
            {renderRow(t('cpeDetails.details.timeline.dateRegistered'), convertToUtc(account?.registered))}
            {renderRow(t('cpeDetails.details.timeline.dateVerified'), convertToUtc(account?.verified))}
          </dl>
        </section>
      </div>

      {/* Controls */}
      <div className="dc-pair">
        <section className="dc-card">
          <div className="dc-card-hd">
            <span className="dc-card-icon">
              <ShieldCheck size={16} strokeWidth={1.75} />
            </span>
            <div className="dc-card-title">
              <h3 className="bd-h3">{t('cpeDetails.details.services.title')}</h3>
              <span className="bd-meta">{t('cpeDetails.details.services.subtitle')}</span>
            </div>
          </div>
          <div className="dc-settings">
            {serviceSettings.map((s) => (
              <div className="dc-setting" key={s.id}>
                <div className="dc-setting-text">
                  <label className="dc-setting-label" htmlFor={s.id}>
                    {s.label}
                  </label>
                  <span className="bd-meta">{s.desc}</span>
                </div>
                <div className="dc-setting-ctrl">
                  <span className={`dc-setting-state bd-mono${s.checked ? ' on' : ''}`}>
                    {s.checked ? t('cpeDetails.details.active') : t('cpeDetails.details.inactive')}
                  </span>
                  <button
                    type="button"
                    role="switch"
                    id={s.id}
                    aria-checked={!!s.checked}
                    aria-label={s.label}
                    className={`bd-switch${s.checked ? ' on' : ''}`}
                    onClick={() => s.onChange(!s.checked)}
                  />
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="dc-card">
          <div className="dc-card-hd">
            <span className="dc-card-icon">
              <Cpu size={16} strokeWidth={1.75} />
            </span>
            <div className="dc-card-title">
              <h3 className="bd-h3">{t('cpeDetails.details.agent.title')}</h3>
              <span className="bd-meta">{t('cpeDetails.details.agent.subtitle')}</span>
            </div>
            <span className={`bd-chip dc-card-chip ${isAgentRunning ? 'mint' : ''}`}>
              <span className="dot" />
              {isAgentRunning ? t('cpeDetails.details.agent.running') : t('cpeDetails.details.agent.stopped')}
            </span>
          </div>
          <div className="dc-agent-actions">
            <button
              type="button"
              className="bd-btn primary"
              onClick={handleStartBootloader}
              disabled={isAgentRunning === true}
              title={
                isAgentRunning === true
                  ? t('cpeDetails.details.agent.alreadyRunning')
                  : t('cpeDetails.details.agent.startHint')
              }
            >
              <Play size={15} strokeWidth={2} />
              {t('cpeDetails.details.agent.start')}
            </button>
            <button
              type="button"
              className="bd-btn danger-soft"
              onClick={handleStopBootloader}
              disabled={isAgentRunning === false}
              title={
                isAgentRunning === false
                  ? t('cpeDetails.details.agent.alreadyStopped')
                  : t('cpeDetails.details.agent.stopHint')
              }
            >
              <Square size={14} strokeWidth={2} />
              {t('cpeDetails.details.agent.stop')}
            </button>
          </div>
        </section>

        {showConfirmationModal && (
          <ConfirmationModal
            show={showConfirmationModal}
            title={confirmationTitle}
            description={confirmationMessage}
            onCancel={() => setShowConfirmationModal(false)}
            onConfirm={() => {
              setShowConfirmationModal(false);

              if (confirmationAction === 'start') {
                showToastMessage(t('cpeDetails.details.agent.startedToast'));
                setIsAgentRunning(true);
              } else if (confirmationAction === 'stop') {
                showToastMessage(t('cpeDetails.details.agent.stoppedToast'));
                setIsAgentRunning(false);
              }

              handleBootloaderSubmit()
            }}
          />
        )}
      </div>

      {/* Domain lists */}
      <div className="dc-pair">
        <section className="dc-card">
          <div className="dc-card-hd">
            <span className="dc-card-icon danger">
              <Ban size={16} strokeWidth={1.75} />
            </span>
            <div className="dc-card-title">
              <h3 className="bd-h3">{t('cpeDetails.details.domains.blockedTitle')}</h3>
              <span className="bd-meta">{t('cpeDetails.details.domains.blockedSubtitle')}</span>
            </div>
            <span className="bd-chip dc-card-chip bd-mono">{globalBlocked?.length ?? 0}</span>
          </div>
          <div className="dc-domain-form">
            <label className="bd-search dc-domain-input">
              <Ban size={14} strokeWidth={1.75} />
              <input
                value={currentTypedBlocklist}
                onChange={(e) => addBlockedDomain(e.target.value)}
                ref={inputRef}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    addToBlocklistFilters();
                  }
                }}
                aria-label={t('cpeDetails.details.domains.blockAriaLabel')}
                aria-invalid={!!error}
                placeholder={t('cpeDetails.details.domains.blockPlaceholder')}
              ></input>
            </label>
            <button type="button" className="bd-btn primary" onClick={addToBlocklistFilters}>
              <Plus size={15} strokeWidth={2} />
              {t('cpeDetails.details.domains.addDomain')}
            </button>
          </div>
          {error && (
            <p className="dc-error" role="alert">
              {t(error)}
            </p>
          )}
          <div className="dc-domain-scroll">
            {!globalBlocked?.length ? (
              <p className="dc-empty bd-meta">{t('cpeDetails.details.domains.noBlocked')}</p>
            ) : (
              <ul className="dc-domain-list" id="filter-map">
                {globalBlocked.map((filter, index) => (
                  <li id="list-item" className="dc-domain" key={'bl' + index}>
                    <span className="dc-domain-dot danger" />
                    <span className="dc-domain-name bd-mono bd-truncate" title={filter}>
                      {filter}
                    </span>
                    <button
                      type="button"
                      className="dc-domain-remove"
                      aria-label={t('cpeDetails.details.domains.removeDomain', { domain: filter })}
                      title={t('cpeDetails.details.domains.remove')}
                      onClick={() => removeBlocklist(filter)}
                    >
                      <X size={14} strokeWidth={2} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        <section className="dc-card">
          <div className="dc-card-hd">
            <span className="dc-card-icon">
              <CircleCheck size={16} strokeWidth={1.75} />
            </span>
            <div className="dc-card-title">
              <h3 className="bd-h3">{t('cpeDetails.details.domains.allowedTitle')}</h3>
              <span className="bd-meta">{t('cpeDetails.details.domains.allowedSubtitle')}</span>
            </div>
            <span className="bd-chip dc-card-chip bd-mono">{globalAllow?.length ?? 0}</span>
          </div>
          <div className="dc-domain-form">
            <label className="bd-search dc-domain-input">
              <CircleCheck size={14} strokeWidth={1.75} />
              <input
                value={currentTypedWhitelist}
                onChange={(e) => addAllowedDomain(e.target.value)}
                ref={inputRef}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    addToWhitelistFilters();
                  }
                }}
                aria-label={t('cpeDetails.details.domains.allowAriaLabel')}
                aria-invalid={!!errorW}
                placeholder={t('cpeDetails.details.domains.allowPlaceholder')}
              ></input>
            </label>
            <button type="button" className="bd-btn primary" onClick={addToWhitelistFilters}>
              <Plus size={15} strokeWidth={2} />
              {t('cpeDetails.details.domains.addDomain')}
            </button>
          </div>
          {errorW && (
            <p className="dc-error" role="alert">
              {t(errorW)}
            </p>
          )}
          <div className="dc-domain-scroll">
            {!globalAllow?.length ? (
              <p className="dc-empty bd-meta">{t('cpeDetails.details.domains.noAllowed')}</p>
            ) : (
              <ul className="dc-domain-list" id="filter-map">
                {globalAllow.map((filter, index) => (
                  <li id="list-item" className="dc-domain" key={'wl' + index}>
                    <span className="dc-domain-dot" />
                    <span className="dc-domain-name bd-mono bd-truncate" title={filter}>
                      {filter}
                    </span>
                    <button
                      type="button"
                      className="dc-domain-remove"
                      aria-label={t('cpeDetails.details.domains.removeDomain', { domain: filter })}
                      title={t('cpeDetails.details.domains.remove')}
                      onClick={() => removeWhitelist(filter)}
                    >
                      <X size={14} strokeWidth={2} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};
